export class ApiError extends Error {
  constructor(
    public statusCode: number,
    message: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiClientConfig {
  baseUrl: string;
  onError?: (error: ApiError) => void;
}

export class ApiClient {
  private readonly baseUrl: string;
  private readonly onError?: (error: ApiError) => void;

  constructor(config: ApiClientConfig) {
    this.baseUrl = config.baseUrl.replace(/\/$/, '');
    this.onError = config.onError;
  }

  private csrfToken(): string | undefined {
    if (typeof document === 'undefined') return undefined;
    const match = document.cookie
      .split('; ')
      .find((cookie) => cookie.startsWith('omni_csrf='));
    return match ? decodeURIComponent(match.slice('omni_csrf='.length)) : undefined;
  }

  private headers(options?: RequestInit, includeCsrf = false): Headers {
    const headers = new Headers(options?.headers);
    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
    if (includeCsrf) {
      const token = this.csrfToken();
      if (token) headers.set('x-csrf-token', token);
    }
    return headers;
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      const message =
        typeof errorBody?.message === 'string'
          ? errorBody.message
          : response.statusText || 'Request failed';
      const error = new ApiError(response.status, message, errorBody);
      this.onError?.(error);
      if (response.status === 401 && typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('omni:unauthorized'));
      }
      throw error;
    }

    if (response.status === 204) return undefined as T;
    const contentType = response.headers.get('content-type');
    return contentType?.includes('application/json')
      ? response.json()
      : (undefined as T);
  }

  private async request<T>(
    endpoint: string,
    method: string,
    data?: unknown,
    options?: RequestInit,
  ): Promise<T> {
    const isSafe = ['GET', 'HEAD', 'OPTIONS'].includes(method);
    const retries = isSafe ? 2 : 0;
    let attempt = 0;
    while (true) {
      try {
        const response = await fetch(`${this.baseUrl}${endpoint}`, {
          ...options,
          method,
          credentials: 'include',
          headers: this.headers(options, !isSafe),
          body: data === undefined ? undefined : JSON.stringify(data),
        });
        return await this.handleResponse<T>(response);
      } catch (error) {
        const isNetworkError =
          error instanceof TypeError &&
          /network|fetch/i.test(error.message);
        if (!isNetworkError || attempt >= retries) throw error;
        attempt += 1;
        await new Promise((resolve) => setTimeout(resolve, 250 * attempt));
      }
    }
  }

  get<T>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, 'GET', undefined, options);
  }

  post<T>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, 'POST', data, options);
  }

  patch<T>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, 'PATCH', data, options);
  }

  delete<T>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, 'DELETE', undefined, options);
  }

  deleteWithBody<T>(
    endpoint: string,
    data: unknown,
    options?: RequestInit,
  ): Promise<T> {
    return this.request<T>(endpoint, 'DELETE', data, options);
  }
}

let apiClientInstance: ApiClient | null = null;

export function initializeApiClient(config: ApiClientConfig): void {
  apiClientInstance = new ApiClient(config);
}

export function getApiClient(): ApiClient {
  if (!apiClientInstance) {
    throw new Error('ApiClient has not been initialized');
  }
  return apiClientInstance;
}
